/**
 * Device Registration Service
 *
 * Registers/updates the current device with the backend for subscription abuse detection.
 * This is observability-only - no enforcement or blocking based on device count.
 */

import { getAccessToken, isAuthConfigured } from './authService';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Device information from the Electron main process */
export interface DeviceInfo {
  fingerprint: string;
  deviceName: string;
  platform: 'darwin' | 'win32' | 'linux' | 'unknown';
}

/** Device record from the database */
export interface DeviceRecord {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string;
  platform: string;
  first_seen_at: string;
  last_seen_at: string;
}

/**
 * Register or update the current device for the authenticated user.
 *
 * Returns null if:
 * - API is not configured
 * - User is not authenticated
 * - Operation fails (silent failure - device registration is not critical)
 */
export async function registerDevice(deviceInfo: DeviceInfo): Promise<DeviceRecord | null> {
  if (!isAuthConfigured() || !API_URL) {
    console.log('[DeviceService] API not configured, skipping device registration');
    return null;
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return null;
    }

    const response = await fetch(`${API_URL}/devices/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        device_fingerprint: deviceInfo.fingerprint,
        device_name: deviceInfo.deviceName,
        platform: deviceInfo.platform,
      }),
    });

    if (!response.ok) {
      console.error('[DeviceService] Failed to register device:', response.status);
      return null;
    }

    const data = await response.json();

    if (!data.success) {
      console.error('[DeviceService] Registration returned failure');
      return null;
    }

    console.log('[DeviceService] Device registered/updated:', deviceInfo.fingerprint.substring(0, 16) + '...');
    return data.device as DeviceRecord;
  } catch (error) {
    console.error('[DeviceService] Unexpected error during device registration:', error);
    return null;
  }
}

/**
 * Get list of devices for the current user.
 * Returns empty array if not authenticated or on error.
 */
export async function getUserDevices(): Promise<DeviceRecord[]> {
  if (!isAuthConfigured() || !API_URL) {
    return [];
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return [];
    }

    const response = await fetch(`${API_URL}/devices`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      console.error('[DeviceService] Failed to fetch user devices:', response.status);
      return [];
    }

    const data = await response.json();
    return (data.devices || []) as DeviceRecord[];
  } catch (error) {
    console.error('[DeviceService] Unexpected error fetching devices:', error);
    return [];
  }
}

/**
 * Get count of unique devices for the current user.
 */
export async function getDeviceCount(): Promise<number> {
  const devices = await getUserDevices();
  return devices.length;
}
